import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import { GET_TRUST_CENTER_ADMIN_USERS } from "../../../../apis/trust-center-apis/trustCenterApis";
import {
  outlinedButtonSx,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import {
  DialogHeader,
  dialogActionsSx,
  dialogPaperProps,
} from "../../components/DialogParts";

// Picks the approvers: the admins who accept or decline access requests and
// get an email for each one. Only the customer's admins can be chosen, and
// at least one has to stay (the server checks both again).
const ApproversDialog = ({ open, emails, onSave, onClose }) => {
  const theme = useTheme();
  const [chosen, setChosen] = useState(emails);
  const [saving, setSaving] = useState(false);
  const { data, loading, error } = useQuery(GET_TRUST_CENTER_ADMIN_USERS, {
    skip: !open,
    fetchPolicy: "cache-and-network",
  });
  const admins = (data?.getTrustCenterAdminUsers || []).map((a) => a.email);

  useEffect(() => {
    if (open) {
      setChosen(emails);
      setSaving(false);
    }
    // Reset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    setSaving(true);
    const ok = await onSave(chosen);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={dialogPaperProps}
    >
      <DialogHeader
        title="Approvers"
        subtitle="Approvers accept or decline document requests and get an email for each new one."
        onClose={() => !saving && onClose()}
      />
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>
            Could not load your admins. Close this and try again.
          </Alert>
        )}
        <Autocomplete
          multiple
          filterSelectedOptions
          options={admins}
          value={chosen}
          loading={loading}
          onChange={(_, value) => setChosen(value)}
          noOptionsText={
            loading ? "Loading admins…" : "No other admins to add."
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Approvers"
              placeholder={chosen.length ? "" : "Choose admins"}
              helperText="Only people with the Admin role are listed. Keep at least one approver."
            />
          )}
        />
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1.5,
            color: theme.customColors.text.secondary,
          }}
        >
          Other admins can see requests but cannot accept or decline them, or
          change this list.
        </Typography>
      </DialogContent>
      <DialogActions sx={dialogActionsSx(theme)}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={saving}
          sx={outlinedButtonSx(theme)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || chosen.length === 0}
          sx={primaryButtonSx(theme)}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Above the access requests list: who may accept or decline requests.
// `canApprove`: the signed-in admin is one of them (only they may change
// the list). No approvers yet: every admin may, until one saves the list.
const ApproversCard = ({ emails = [], canApprove, onSave }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  return (
    <Paper
      elevation={0}
      aria-label="Approvers"
      component="section"
      sx={{
        px: 2.5,
        py: 1.75,
        mb: 2,
        borderRadius: "12px",
        border: `1px solid ${theme.customColors.border.main}`,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <HowToRegOutlinedIcon
            sx={{ color: theme.customColors.icons.primary, mt: 0.25 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }}>Approvers</Typography>
            {emails.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: theme.customColors.text.secondary }}
              >
                No approvers yet, so every admin can accept or decline and
                nobody is emailed. Choose who approves requests.
              </Typography>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{ color: theme.customColors.text.secondary, mb: 0.75 }}
                >
                  Only these admins can accept or decline requests. They get an
                  email for each new one.
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  sx={{ flexWrap: "wrap", rowGap: 0.75 }}
                >
                  {emails.map((email) => (
                    <Chip
                      key={email}
                      size="small"
                      label={email}
                      sx={{
                        backgroundColor: theme.customColors.background.light,
                      }}
                    />
                  ))}
                </Stack>
                {!canApprove && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.75,
                      color: theme.customColors.text.secondary,
                    }}
                  >
                    You are not an approver. Ask one of them to add you.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Stack>
        {canApprove && (
          <Button
            variant="outlined"
            onClick={() => setOpen(true)}
            sx={{ ...outlinedButtonSx(theme), flexShrink: 0 }}
          >
            {emails.length ? "Change approvers" : "Choose approvers"}
          </Button>
        )}
      </Stack>
      <ApproversDialog
        open={open}
        emails={emails}
        onSave={onSave}
        onClose={() => setOpen(false)}
      />
    </Paper>
  );
};

export default ApproversCard;
